import { Lookup as WebSDKLookup } from '@zambezi/sdk/dropdown-list';
import classNames from 'classnames';
import React, { ForwardedRef, useId } from 'react';
import FormControl from '../FormControl';
import Spinner from '../Spinner';
import './Lookup.scss';

interface Option<T = string> {
  label: string;
  value: T;
}

export interface LookupProps<T> {
  label: React.ReactNode | React.ReactNode[];
  name: string;
  suggestions?: Option<T>[] | T[];
  placeholder?: string;
  disabled?: boolean;
  disabledItemsIndices?: number[];
  required?: boolean;
  error?: string | boolean;
  value?: string;
  tooltip?: string | React.ReactNode | React.ReactNode[];
  tooltipAriaLabel?: string;
  helpText?: React.ReactNode | React.ReactNode[];
  hideLabel?: boolean;
  pii?: boolean;
  inputProps?: React.HTMLAttributes<HTMLElement>;
  fullWidth?: boolean;
  isLoading?: boolean;
  suggestionsFetchRequested?: ({ value }: { value: string }) => void;
  suggestionRenderer?: (label: string) => React.ReactNode;
  selectedSuggestionRenderer?: (label: string) => string;
  onChange?: (value: T) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

function SelectInner<T>(
  {
    label,
    placeholder,
    disabled = false,
    disabledItemsIndices = [],
    required = false,
    pii = false,
    value,
    error,
    suggestions = [],
    tooltip,
    tooltipAriaLabel,
    helpText,
    hideLabel = false,
    inputProps = {},
    isLoading = false,
    fullWidth,
    suggestionsFetchRequested,
    suggestionRenderer,
    onChange,
    onBlur,
    onFocus
  }: LookupProps<T>,
  ref: ForwardedRef<HTMLSelectElement>
) {
  const id = useId();

  const getOptionLabel = (suggestion: Option | string | number): string => {
    switch (typeof suggestion) {
      case 'string':
        return suggestion;
      case 'number':
        return `${suggestion}`;
      default:
        return suggestion.label;
    }
  };

  const getOptionValue = (suggestion: Option<T>): T => {
    switch (typeof suggestion) {
      case 'string':
      case 'number':
        return suggestion;
      default:
        return suggestion.value;
    }
  };

  const suggestionLabelRenderer = (label: string) =>
    suggestionRenderer ? suggestionRenderer(label) : label;

  const renderSuggestion = (suggestion: Option | string | number) => {
    const suggestionLabel = getOptionLabel(suggestion);
    const suggestionTestId = suggestionLabel;
    return (
      <span data-cy={suggestionTestId} data-testid={suggestionTestId}>
        {suggestionLabelRenderer(suggestionLabel)}
      </span>
    );
  };

  return (
    <FormControl
      id={id}
      label={label}
      tooltip={tooltip}
      tooltipAriaLabel={tooltipAriaLabel}
      error={error}
      helpText={helpText}
      required={required}
      hideLabel={hideLabel}
      pii={pii}
    >
      <WebSDKLookup
        id={id}
        placeholder={placeholder}
        suggestions={suggestions}
        getSuggestionValue={getOptionLabel}
        renderSuggestion={renderSuggestion}
        isDisabled={disabled}
        disabledItemsIndices={disabledItemsIndices}
        isError={!!error}
        value={value}
        suggestionsFetchRequested={suggestionsFetchRequested}
        onBlur={onBlur}
        onFocus={onFocus}
        onSuggestionSelected={(_, { suggestion }) => {
          onChange && onChange(getOptionValue(suggestion));
        }}
        renderNoResultsMessage={() => (isLoading ? <Spinner /> : 'No matches found')}
        inputProps={{
          'aria-describedby': error ? `error-${id}` : undefined,
          'aria-required': required,
          ...inputProps
        }}
        className={classNames('mss-select-input', {
          'mss-select-input--full-width': fullWidth
        })}
        // have to ignore this because WebSDK don't export LookupRef
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
      />
    </FormControl>
  );
}

export default React.forwardRef(SelectInner) as <T>(
  props: LookupProps<T> & { ref?: ForwardedRef<HTMLSelectElement> }
) => ReturnType<typeof SelectInner>;
